<script setup lang="ts">
const { state: agentState, isFollowing, isInProgress } = useAgent()
const { currentTranscript } = useConversations()
const { isOffline } = useCache()
const container = useTemplateRef("container")
const { addInterfaceEventListener, removeInterfaceEventListener } = useEvents()

const onContainerScrollUpdated = (scroll: { bottom: number }) => {
  agentState.value.scrollBottom = scroll.bottom
}

const onScrollToBottomRequested = () => {
  if (!isDefined(container.value))
    throw new Error("Cannot scroll as container is not defined")
  container.value.scrollTo({ bottom: 0, behavior: "smooth" })
}

const onContainerMounted = async () => {
  await sleep(100)
  container.value?.scrollTo({ bottom: 0 })
}

onMounted(async () => {
  addInterfaceEventListener("scrollToLatestAgentMessage", onScrollToBottomRequested)
})
onUnmounted(() => {
  removeInterfaceEventListener("scrollToLatestAgentMessage", onScrollToBottomRequested)
})
</script>

<template>
  <interface-scrollbar
    ref="container"
    vertical
    observe-mutations
    @scroll="onContainerScrollUpdated"
    @initialized="onContainerMounted"
  >
    <div class="container-inner">
      <template v-for="turn, key in currentTranscript" :key>
        <template v-if="'user' in turn">
          <panel-agent-input-turn
            v-if="typeof turn.user !== 'string' && 'input' in turn.user"
            :input="turn.user.input"
          />
          <div v-else>
            ???
          </div>
        </template>
        <template v-else-if="'agent' in turn">
          <panel-agent-output
            v-if="'output' in turn.agent"
            :output="turn.agent.output"
          />
          <panel-agent-status
            v-else-if="'status' in turn.agent"
            :status="turn.agent.status"
          />
          <panel-agent-error
            v-else-if="'errored' in turn.agent"
            :error="turn.agent.errored"
          />
          <div v-else-if="!('ended' in turn.agent)">
            ???
          </div>
        </template>
      </template>
      <div v-if="isInProgress" class="agent-in-progress">
        Thinking
        <div class="dots">
          <div>•</div>
          <div>•</div>
          <div>•</div>
        </div>
      </div>
      <div class="agent-sticky">
        <div v-if="isOffline() || !isFollowing" class="agent-toast">
          <panel-footer-agent-offline v-if="isOffline()" class="agent-footer-message" />
          <panel-footer-agent-scroll v-else-if="!isFollowing" class="agent-footer-message" />
        </div>
        <panel-agent-input-editor />
      </div>
    </div>
  </interface-scrollbar>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.container-inner
  @apply max-w-screen-sm mx-auto
  @apply pt-[#{$panel-p-default}]
  @apply space-y-[#{$panel-p-default}]

.panel-agent-inner
  @apply h-full w-full

.agent-timestamp
  @apply flex items-center justify-center mt-5 gap-1
  @apply t-panel-xs text-[#{$panel-agent-t-timestamp}]

.agent-sticky
  @apply sticky bottom-0
  @apply px-[#{$panel-p-default}] pb-[#{$panel-p-default}] // screen bottom

.agent-in-progress
  @apply inline-flex items-center
  @apply mx-[#{$panel-p-default}] rounded-full outline outline-$global-violet-accent
  @apply px-[8px] py-[4px]
  @apply t-panel-sm text-[#{$global-t-secondary}]
  @apply gap-1
  .agent
    @apply text-[#{$global-violet-primary}]
  .dots
    @apply flex items-center
    @apply text-[#{$global-bg-icon-secondary}]
    :nth-child(1)
      @apply animate-pulse
      @apply animate-delay-0
    :nth-child(2)
      @apply animate-pulse
      @apply animate-delay-250
    :nth-child(3)
      @apply animate-pulse
      @apply animate-delay-500

.agent-toast
  @apply mb-2 flex items-center justify-center relative h-0

.agent-footer-message
  @apply absolute bottom-0
</style>
